import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  isRead: boolean;
  payloadJson: { link?: string } | null;
  createdAt: string;
}

const POLL_MS = 30_000;

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api<{ count: number }>('/notifications/unread-count'),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
  });

  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api<NotificationItem[]>('/notifications?limit=15'),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api<{ count: number }>('/notifications/read-all', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/notifications/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  // Cierre al click fuera
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = unread.data?.count ?? 0;
  const items = list.data ?? [];

  function onItemClick(n: NotificationItem) {
    if (!n.isRead) markRead.mutate(n.id);
    const link = n.payloadJson?.link;
    if (link) {
      setOpen(false);
      navigate(link);
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-sm border border-border bg-card text-ink-2 hover:border-foreground hover:text-foreground transition-colors"
        aria-label={count > 0 ? `${count} notificaciones sin leer` : 'Notificaciones'}
      >
        <Bell className="h-4 w-4" />
        {count > 0 ? (
          <span
            className="absolute -top-1 -right-1 inline-block h-3 w-3 rounded-full border-2 border-card"
            style={{ backgroundColor: '#1B23B8' }}
            aria-hidden
          />
        ) : null}
      </button>

      {open ? (
        <div className="absolute left-full top-0 ml-2 w-80 max-w-[90vw] rounded-sm border border-border bg-card shadow-lg z-50 overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold">
              Notificaciones
              {count > 0 ? (
                <span className="font-normal text-muted-foreground"> · {count} nuevas</span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={count === 0 || markAllRead.isPending}
              className="text-xs font-medium text-icaria-600 hover:text-icaria-700 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Marcar todas
            </button>
          </header>
          <div className="max-h-96 overflow-y-auto">
            {list.isLoading ? (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">Cargando…</p>
            ) : list.isError ? (
              <p className="px-4 py-6 text-sm text-destructive text-center">
                {list.error instanceof ApiError ? list.error.message : 'Error al cargar'}
              </p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-sm text-muted-foreground text-center">
                No tienes notificaciones.
              </p>
            ) : (
              <ul>
                {items.map((n) => (
                  <li key={n.id} className="border-b border-border last:border-b-0">
                    <button
                      type="button"
                      onClick={() => onItemClick(n)}
                      className={cn(
                        'w-full text-left px-4 py-3 transition-colors flex gap-3',
                        n.isRead
                          ? 'bg-card hover:bg-paper'
                          : 'bg-icaria-100 hover:bg-icaria-200/60',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-1.5 inline-block h-1.5 w-1.5 rounded-full shrink-0',
                          n.isRead ? 'bg-transparent' : 'bg-icaria-600',
                        )}
                        aria-hidden
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-medium leading-tight text-foreground">
                          {n.title}
                        </span>
                        {n.body ? (
                          <span className="block text-xs text-muted-foreground line-clamp-2 whitespace-pre-line mt-0.5 leading-snug">
                            {n.body}
                          </span>
                        ) : null}
                        <span className="block font-mono text-[10px] uppercase tracking-cap text-muted-foreground mt-1">
                          {formatRelative(n.createdAt)}
                        </span>
                      </span>
                      {!n.isRead ? (
                        <span
                          className="text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            markRead.mutate(n.id);
                          }}
                          aria-label="Marcar como leída"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      ) : (
                        <span
                          className="text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMutation.mutate(n.id);
                          }}
                          aria-label="Borrar notificación"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffSec = Math.round((now - d.getTime()) / 1000);
  if (diffSec < 60) return 'hace un momento';
  if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
  if (diffSec < 86400 * 7) return `hace ${Math.floor(diffSec / 86400)} d`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}
